import React, { useState, useEffect } from "react";
import { Home, ArrowLeft, HelpCircle } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { FONT_SIZES, FONT_FAMILY, APP_NAME, APP_VERSION, ICON_SIZES } from "../../constants/appConfig";
import HelpPanel from "../ui/HelpPanel";
import {
  WhatsNewSidebar,
  WhatsNewToggleButton,
} from "../features/WhatsNewSidebar";

// ═══════════════════════════════════════════════════════════════
// USER BADGE — Shows who is logged in
// Designed to be large and obvious for non-technical board members.
// Initials circle + full name + role label + online dot.
// ═══════════════════════════════════════════════════════════════

const getInitials = (fullName) => {
  if (!fullName) return "?";
  const parts = fullName.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return parts[0].substring(0, 2).toUpperCase();
};

const ROLE_CONFIG = {
  admin: { label: "Admin", bg: "rgba(239,68,68,0.25)", color: "#fca5a5" },
  manager: { label: "Manager", bg: "rgba(59,130,246,0.25)", color: "#93c5fd" },
  board: { label: "Board", bg: "rgba(34,197,94,0.25)", color: "#86efac" },
};

const UserBadge = ({ palette }) => {
  const { userProfile } = useApp();
  if (!userProfile) return null;

  const initials = getInitials(userProfile.full_name);
  const role = ROLE_CONFIG[userProfile.role] || {
    label: userProfile.role || "User",
    bg: "rgba(255,255,255,0.15)",
    color: "rgba(255,255,255,0.8)",
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        marginRight: 8,
      }}
    >
      {/* Initials Circle with Online Dot */}
      <div style={{ position: "relative", flexShrink: 0 }}>
        <div
          style={{
            width: 38,
            height: 38,
            borderRadius: "50%",
            background: palette.accent1,
            border: "2px solid rgba(255,255,255,0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 15,
            fontWeight: 800,
            color: "#fff",
            fontFamily: FONT_FAMILY,
            letterSpacing: 0.5,
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
          }}
        >
          {initials}
        </div>
        {/* Online presence dot */}
        <div
          style={{
            position: "absolute",
            bottom: 0,
            right: 0,
            width: 10,
            height: 10,
            borderRadius: "50%",
            background: "#22c55e",
            border: "2px solid rgba(0,0,0,0.3)",
            boxShadow: "0 0 0 1px rgba(255,255,255,0.2)",
          }}
        />
      </div>

      {/* Name + Role */}
      <div style={{ lineHeight: 1.2 }}>
        <div
          style={{
            fontSize: FONT_SIZES.sm,
            fontWeight: 700,
            color: palette.textOnGrad,
            whiteSpace: "nowrap",
          }}
        >
          {userProfile.full_name || "Unknown User"}
        </div>
        <span
          style={{
            display: "inline-block",
            fontSize: 10,
            fontWeight: 700,
            padding: "1px 7px",
            borderRadius: 4,
            background: role.bg,
            color: role.color,
            textTransform: "uppercase",
            letterSpacing: 0.5,
            marginTop: 2,
          }}
        >
          {role.label}
        </span>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// HEADER BUTTON — Reusable nav button for Zone 1
// ═══════════════════════════════════════════════════════════════

const HeaderBtn = ({ icon: Icon, label, onClick }) => {
  const { palette } = useApp();
  return (
    <button
      onClick={onClick}
      style={{
        background: "rgba(255,255,255,0.15)",
        border: "1px solid rgba(255,255,255,0.2)",
        borderRadius: 8,
        padding: "8px 12px",
        cursor: "pointer",
        color: palette.textOnGrad,
        display: "flex",
        alignItems: "center",
        gap: 6,
        fontSize: FONT_SIZES.sm,
        fontFamily: FONT_FAMILY,
        transition: "all 0.2s",
      }}
      onMouseEnter={(e) =>
        (e.currentTarget.style.background = "rgba(255,255,255,0.25)")
      }
      onMouseLeave={(e) =>
        (e.currentTarget.style.background = "rgba(255,255,255,0.15)")
      }
    >
      <Icon size={ICON_SIZES.sm} />
      {label}
    </button>
  );
};

// ═══════════════════════════════════════════════════════════════
// HELP BUTTON — Distinct style so users know it's not navigation
// ═══════════════════════════════════════════════════════════════

const HelpBtn = ({ onClick }) => (
  <button
    onClick={onClick}
    title="How this module works"
    style={{
      background: "rgba(255,255,255,0.22)",
      border: "1px solid rgba(255,255,255,0.4)",
      borderRadius: 8,
      padding: "8px 11px",
      cursor: "pointer",
      color: "#fff",
      display: "flex",
      alignItems: "center",
      gap: 5,
      fontSize: FONT_SIZES.xs,
      fontFamily: FONT_FAMILY,
      fontWeight: 700,
      letterSpacing: "0.04em",
      transition: "all 0.2s",
    }}
    onMouseEnter={(e) =>
      (e.currentTarget.style.background = "rgba(255,255,255,0.35)")
    }
    onMouseLeave={(e) =>
      (e.currentTarget.style.background = "rgba(255,255,255,0.22)")
    }
  >
    <HelpCircle size={ICON_SIZES.sm} />
    Help
  </button>
);

// ═══════════════════════════════════════════════════════════════
// ZONE 1 HEADER — Navigation bar with user identification
//
// NEW PROP: helpContent (optional)
//   Pass a helpContent object from src/constants/helpContent.js
//   to show a "Help" button that opens the slide-in HelpPanel.
//   Omitting helpContent shows no Help button — zero impact on
//   views that haven't been wired up yet.
//
//   Example:
//     import { HELP } from "../../constants/helpContent";
//     <ZoneOneHeader ... helpContent={HELP.cultivation} />
// ═══════════════════════════════════════════════════════════════

export const ZoneOneHeader = ({
  title,
  subtitle,
  icon: Icon,
  onBack,
  onHome,
  rightContent,
  helpContent, // ← NEW: optional, from helpContent.js
}) => {
  const { palette, navContext } = useApp();
  const [helpOpen, setHelpOpen] = useState(false);
  const [whatsNewOpen, setWhatsNewOpen] = useState(false);

  // Auto-open the help panel when the user arrived via a What's New
  // CTA flagged `ctaOpenHelp`. Fires once per navigation.
  useEffect(() => {
    if (helpContent && navContext?.openHelp) {
      setHelpOpen(true);
    }
  }, [helpContent, navContext?.openHelp]);

  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "14px 24px",
          background: palette.headerBg,
          backdropFilter: "blur(20px)",
          borderBottom: "1px solid rgba(255,255,255,0.1)",
          fontFamily: FONT_FAMILY,
        }}
      >
        {/* Left: Icon + Title */}
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {Icon && (
            <Icon
              size={ICON_SIZES.lg}
              style={{ color: palette.textOnGrad, opacity: 0.9 }}
            />
          )}
          <div>
            <h1
              style={{
                margin: 0,
                fontSize: FONT_SIZES.xxxl,
                fontWeight: 700,
                color: palette.textOnGrad,
              }}
            >
              {title}
            </h1>
            {subtitle && (
              <p
                style={{
                  margin: 0,
                  fontSize: FONT_SIZES.sm,
                  color: palette.textOnGrad,
                  opacity: 0.8,
                }}
              >
                {subtitle}
              </p>
            )}
          </div>
        </div>

        {/* Right: User Badge + Help + Custom Content + Nav Buttons */}
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <UserBadge palette={palette} />
          {helpContent && <HelpBtn onClick={() => setHelpOpen(true)} />}
          <WhatsNewToggleButton onClick={() => setWhatsNewOpen(true)} />
          {rightContent}
          {onHome && <HeaderBtn icon={Home} onClick={onHome} />}
          {onBack && (
            <HeaderBtn icon={ArrowLeft} label="Back" onClick={onBack} />
          )}
        </div>
      </div>

      {/* Help Panel — only mounts when helpContent is provided */}
      {helpContent && (
        <HelpPanel
          isOpen={helpOpen}
          onClose={() => setHelpOpen(false)}
          helpContent={helpContent}
        />
      )}

      <WhatsNewSidebar
        isOpen={whatsNewOpen}
        onClose={() => setWhatsNewOpen(false)}
      />
    </>
  );
};

// ═══════════════════════════════════════════════════════════════
// ZONE 3 FOOTER — Brand bar
// ═══════════════════════════════════════════════════════════════

export const ZoneThreeFooter = () => {
  const { palette, chapterName } = useApp();
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 12,
        padding: "12px 24px",
        background: "rgba(0,0,0,0.08)",
        backdropFilter: "blur(10px)",
        fontFamily: FONT_FAMILY,
        fontSize: FONT_SIZES.xs,
        color: palette.textOnGrad,
        opacity: 0.7,
        borderTop: "1px solid rgba(255,255,255,0.1)",
        marginTop: "auto",
      }}
    >
      <span>
        {APP_NAME} • {chapterName}
      </span>
      <span style={{ margin: "0 4px" }}>•</span>
      <span>
        © {new Date().getFullYear()} ClearView Development Platform. All rights
        reserved.
      </span>
      <span
        style={{
          fontSize: 15,
          fontWeight: 800,
          letterSpacing: "0.05em",
          color: palette.textOnGrad,
          opacity: 1,
          background: "rgba(255,255,255,0.15)",
          padding: "2px 10px",
          borderRadius: 6,
        }}
      >
        v{APP_VERSION}
      </span>
    </div>
  );
};
